import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useMemo, useState } from 'react';
import { z } from 'zod';

import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import {
  CostOverviewFilterBar,
  type CostOverviewViewMode,
  type ComparisonTarget,
} from '@/features/contract-and-cost/cost';
import { EditBottomSheet } from '@/features/contract-and-cost/EditBottomSheet';
import { getMonthsInPeriod } from '@/features/contract-and-cost/period';
import {
  CostStatCards,
  CostSummaryChart,
} from '@/features/software/contract-and-cost/components';
import {
  CostPlanOverviewTable,
  useSoftwareCostDetailBySoftwareId,
  useSoftwareCostOverviewBySoftwareId,
  useSoftwareCostSelectablePlansBySoftwareId,
  useSaveSoftwareCostMonthly,
} from '@/features/software/contract-and-cost/cost';
import { useEditGridState } from '@/features/software/contract-and-cost/hooks/useEditGridState';
import {
  MonthlyEditGrid,
  type MonthlyEditGridDefaultData,
  type MonthlyEditGridPlan,
} from '@/features/software/contract-and-cost/MonthlyEditGrid';
import { handleApiError } from '@/lib/api/error';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

const SearchSchema = z.object({
  viewMode: z.enum(['monthly', 'transition']).default('transition'),
  month: z.number().min(1).max(12).optional(),
  comparisonTarget: z
    .enum(['PREVIOUS_YEAR', 'PREVIOUS_MONTH'])
    .default('PREVIOUS_MONTH'),
});

export const Route = createFileRoute(
  '/_staff/software/$id/_$id/contract-and-cost/_contract-and-cost/cost/$period/',
)({
  component: SoftwareCostDetailPage,
  validateSearch: zodValidator(SearchSchema),
});

function SoftwareCostDetailPage() {
  const { id, period } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { t, i18n } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost.costOverview',
  });
  const { t: tCommon } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost',
  });

  const isMonthly = search.viewMode === 'monthly';
  const monthOptions = useMemo(() => getMonthsInPeriod(period), [period]);
  const { toast } = useToast();
  const {
    editOpen,
    setEditOpen,
    editGridKey,
    editDataRef,
    isPlanSelected,
    handleEditDataChange,
    openEditor,
    closeEditor,
  } = useEditGridState();

  const summaryRegionId = 'software-cost-overview-summary-region';

  const detailQuery = useSoftwareCostDetailBySoftwareId(
    id,
    period,
    search.comparisonTarget,
  );
  const overviewQuery = useSoftwareCostOverviewBySoftwareId(id, period);
  const selectablePlansQuery = useSoftwareCostSelectablePlansBySoftwareId(id);

  const plans = useMemo<MonthlyEditGridPlan[]>(() => {
    const rows = selectablePlansQuery.data?.planNames ?? [];
    return rows.map((planName) => ({ id: planName, label: planName }));
  }, [selectablePlansQuery.data]);

  const gridDefaultDataList = useMemo<MonthlyEditGridDefaultData[]>(() => {
    const rows = detailQuery.data?.data ?? [];
    return rows.map((p) => ({
      planName: p.planName,
      months: p.months.map((m) => ({
        targetYearMonth: m.targetYearMonth,
        plannedAmount: m.plannedCostAmount,
        actualAmount: m.actualCostAmount,
        userCount: m.userCount,
      })),
    }));
  }, [detailQuery.data]);

  const saveCostMutation = useSaveSoftwareCostMonthly(id, monthOptions, {
    onSuccess: () => {
      toast({ title: tCommon('editBottomSheet.saveCostSuccessToast') });
      closeEditor();
    },
    onError: (error) => {
      const { message } = handleApiError(error, i18n);
      toast({ title: message, variant: 'destructive' });
    },
  });

  const currentMonth = search.month ?? new Date().getMonth() + 1;
  const [showSummary, setShowSummary] = useState(false);

  return (
    <div className="relative flex flex-1 flex-col gap-5 overflow-auto px-12 py-5 min-h-0">
      <div id={summaryRegionId} hidden={!showSummary}>
        {overviewQuery.data && (
          <VStack className="gap-5">
            <CostStatCards summary={overviewQuery.data} />
            <CostSummaryChart
              data={overviewQuery.data.months}
              months={monthOptions}
            />
          </VStack>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          className="flex items-center gap-1 transition-colors"
          aria-expanded={showSummary}
          aria-controls={summaryRegionId}
          onClick={() => setShowSummary((prev) => !prev)}
        >
          <Icon
            iconName={showSummary ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            className={cn('size-3.5')}
          />
          <Text
            className={cn(
              'text-labelSmallReg',
              'text-neutral-muted-on-fill',
              'hover:text-neutral-on-fill',
            )}
          >
            {showSummary ? t('summary.collapse') : t('summary.expand')}
          </Text>
        </button>
      </div>

      <CostOverviewFilterBar
        viewMode={search.viewMode}
        onViewModeChange={(mode: CostOverviewViewMode) => {
          navigate({
            search: (prev) => ({
              ...prev,
              viewMode: mode,
              month: mode === 'monthly' ? currentMonth : undefined,
            }),
            replace: true,
          });
        }}
        selectedMonth={isMonthly ? currentMonth : undefined}
        onSelectedMonthChange={(month: number) => {
          navigate({
            search: (prev) => ({ ...prev, month }),
            replace: true,
          });
        }}
        monthOptions={monthOptions}
        comparisonTarget={search.comparisonTarget}
        onComparisonTargetChange={(target: ComparisonTarget) => {
          navigate({
            search: (prev) => ({ ...prev, comparisonTarget: target }),
          });
        }}
      />

      <HStack className="justify-end">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-labelSmall"
          onClick={openEditor}
        >
          <Icon iconName="PencilIcon" className="size-3.5" />
          {tCommon('editButton')}
        </Button>
      </HStack>

      <EditBottomSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        title={tCommon('editBottomSheet.editCostTitle')}
        onSave={() => {
          const data = editDataRef.current;
          if (!data) return;
          saveCostMutation.mutate(data);
        }}
        onCancel={closeEditor}
        saveDisabled={!isPlanSelected}
      >
        <MonthlyEditGrid
          key={editGridKey}
          months={monthOptions}
          plans={plans}
          dataList={gridDefaultDataList}
          onChange={handleEditDataChange}
        />
      </EditBottomSheet>

      {!isMonthly && (
        <CostPlanOverviewTable
          detail={detailQuery.data}
          isPending={detailQuery.isPending}
          months={monthOptions}
          className="flex-1 min-h-0"
        />
      )}
    </div>
  );
}
